import { Icon, IconName } from '@/components/Icon/Icon';
import { cn } from '@/lib/utils';
import React, { ButtonHTMLAttributes, ReactNode } from 'react';

const variants = {
  primary:
    'bg-buttons-backgrounds-primary-standard text-buttons-content-primary-standard hover:bg-buttons-backgrounds-primary-hover hover:text-buttons-content-primary-hover active:bg-buttons-backgrounds-primary-hover active:text-buttons-content-primary-hover disabled:bg-buttons-backgrounds-primary-disabled disabled:text-buttons-content-primary-disabled',
  secondary:
    'bg-buttons-backgrounds-secondary-standard text-buttons-content-secondary-standard hover:bg-buttons-backgrounds-secondary-hover hover:text-buttons-content-secondary-hover active:bg-buttons-backgrounds-secondary-hover active:text-buttons-content-secondary-hover disabled:bg-buttons-backgrounds-secondary-disabled disabled:text-buttons-content-secondary-disabled',
  tertiary:
    'text-buttons-content-tertiary-standard hover:bg-buttons-backgrounds-tertiary-hover hover:text-buttons-content-tertiary-hover active:bg-buttons-backgrounds-tertiary-hover active:text-buttons-content-tertiary-hover disabled:text-buttons-content-tertiary-disabled',
  destructive:
    'bg-buttons-backgrounds-destructive-standard text-buttons-content-primary-standard hover:bg-buttons-backgrounds-destructive-hover hover:text-buttons-content-primary-hover active:bg-buttons-backgrounds-destructive-hover active:text-buttons-content-primary-hover disabled:bg-buttons-backgrounds-destructive-disabled disabled:text-buttons-content-primary-disabled',
  link: '!label-small !py-100 !px-0 text-buttons-content-link-standard hover:text-buttons-content-link-hover hover:underline active:text-buttons-content-link-hover active:underline disabled:text-buttons-content-link-disabled',
  transparent:
    'bg-buttons-backgrounds-transparent text-buttons-content-primary-standard hover:bg-buttons-backgrounds-transparent-hover active:bg-buttons-backgrounds-transparent-pressed disabled:bg-buttons-backgrounds-transparent-disabled disabled:text-buttons-content-primary-disabled',
  glass:
    'backdrop:blur-[2px] bg-buttons-backgrounds-glassy text-buttons-content-primary-standard hover:bg-buttons-backgrounds-glassy-hover hover:text-[#FFF] active:bg-buttons-backgrounds-glassy-pressed active:text-[#FFF] disabled:bg-buttons-backgrounds-glassy-disabled disabled:text-buttons-content-secondary-disabled',
};
const sizes = {
  sm: 'label-small py-200 px-300 gap-200',
  md: 'label-medium py-300 px-400 gap-200',
  lg: 'label-large py-400 px-500 gap-200',
};
const iconSizes = {
  sm: 'p-200',
  md: 'p-300',
  lg: 'p-400',
};
const iconDimensions = {
  sm: 'w-[1rem] h-[1rem]', // TODO: Adaptive spacings
  md: 'w-[1.25rem] h-[1.25rem]',
  lg: 'w-[1.5rem] h-[1.5rem]',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  children?: ReactNode;
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'destructive'
    | 'link'
    | 'transparent'
    | 'glass';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  iconLeading?: IconName;
  iconTrailing?: IconName;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className = '',
      variant = 'primary',
      size = 'md',
      children,
      iconLeading,
      iconTrailing,
      ...props
    },
    ref,
  ) => {
    const onlyIcon = (iconLeading || iconTrailing) && !children;

    return (
      <button
        ref={ref}
        className={cn(
          'flex select-none items-center justify-center gap-200 rounded-200 transition-colors disabled:cursor-not-allowed',
          variants[variant],
          onlyIcon ? iconSizes[size] : sizes[size],
          className,
        )}
        {...props}
      >
        {iconLeading && (
          <Icon className={iconDimensions[size]} icon={iconLeading} />
        )}
        {children && children}
        {iconTrailing && (
          <Icon className={iconDimensions[size]} icon={iconTrailing} />
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';
